import { createContext, useContext, useMemo, type ReactNode } from 'react'
import { useEffectiveFlags } from '@/hooks/useFeatureFlags'

type FeatureFlagsContextValue = {
  flags: Record<string, boolean>
  isLoading: boolean
}

const FeatureFlagsContext = createContext<FeatureFlagsContextValue | null>(null)

export function FeatureFlagsProvider({ children }: { children: ReactNode }) {
  const { data, isLoading } = useEffectiveFlags()

  const value = useMemo<FeatureFlagsContextValue>(
    () => ({ flags: data ?? {}, isLoading }),
    [data, isLoading],
  )

  return (
    <FeatureFlagsContext.Provider value={value}>{children}</FeatureFlagsContext.Provider>
  )
}

/**
 * The raw effective map plus its loading state.
 *
 * `useFeatureFlag` below collapses "still loading" and "explicitly enabled" into
 * the same `true`, which is right for in-page affordances and wrong for a route
 * guard — a guard that treats loading as enabled renders the page and then yanks
 * it, and one that treats loading as disabled redirects an entitled user to
 * /no-access before the map lands. `FeatureGated` needs to tell the two apart,
 * so it reads this instead.
 */
export function useFeatureFlags(): FeatureFlagsContextValue {
  const ctx = useContext(FeatureFlagsContext)
  if (!ctx) throw new Error('useFeatureFlags must be used within a FeatureFlagsProvider')
  return ctx
}

/**
 * Reads a single feature flag, mirroring `usePermission().can()` ergonomics.
 * Fails open: while the effective map is loading, or the key is absent, returns
 * `true` so gated features never flash-hide.
 *
 * NOT A SECURITY CONTROL. This is for in-page affordances (a button, a tab) and
 * deliberately errs towards showing them. Enforcement is the backend's: every
 * gated controller carries `@RequireFeature`, which fails closed. Never rely on
 * this hook to keep data off a screen.
 *
 * Fails open with NO PROVIDER too, rather than throwing. Its entire contract is
 * "when in doubt, show it", and a missing provider is just another form of
 * doubt — it can only ever reveal UI whose requests the backend still refuses,
 * never hide something. That also keeps the components using it renderable in
 * isolation (the FormRenderer SSR smoke tests mount it with only a
 * QueryClientProvider). `useFeatureFlags` below still throws, because the route
 * guard built on it decides whether to REDIRECT, and getting that wrong on a
 * silently-empty map would black out working pages.
 */
export function useFeatureFlag(key: string): boolean {
  const ctx = useContext(FeatureFlagsContext)
  if (!ctx || ctx.isLoading) return true
  const value = ctx.flags[key]
  return value === undefined ? true : value
}
